<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'

import { useI18n } from '@/app/i18n'
import { switchCompany } from '@/app/session'
import { postCompany } from '@/app/session/api'
import { useAuthStore } from '@/app/stores/auth'
import { errorMessage, successMessage, toast } from '@/shared/composables/useToast'
import AppInput from '@/shared/ui/AppInput.vue'
import AppModal from '@/shared/ui/AppModal.vue'

/**
 * "Add new organization" (wp/shell §4.16a): `POST companies`, switch to the new workspace, then a full reload on its
 * company profile (the wizard) — legacy `window.location.replace('/re/company-profile/')`, software `re` as in legacy.
 * Shown to Clients only (FX-057, see the navbar). The modal stays open on an error.
 */
const visible = defineModel<boolean>('visible', { default: false })
const { t } = useI18n()
const auth = useAuthStore()
const router = useRouter()
const name = ref('')
const error = ref('')

async function save(): Promise<boolean> {
  const label = name.value.trim()
  if (!label) {
    error.value = t('common.validation.required', { field: t('shell.modal.addOrganization.field.name') })
    return false
  }
  error.value = ''
  try {
    const response = await postCompany(label)
    toast.success(successMessage(response))
    auth.update({ workspaces: [...(auth.session?.workspaces ?? []), response.data] })
    await switchCompany(response.data.Id, { reload: false })
    name.value = ''
    await router.replace('/re/company-profile')
    router.go(0)
    return true
  } catch (failure) {
    toast.danger(errorMessage(failure))
    return false
  }
}
</script>

<template>
  <AppModal
    v-model:visible="visible"
    :title="t('shell.modal.addOrganization.title.page')"
    ok-only
    :ok-title="t('common.action.save')"
    :ok-action="save"
  >
    <p class="mb-4">{{ t('shell.modal.addOrganization.hint.body') }}</p>
    <label for="organization-name" class="mb-1 block text-[0.929rem] text-heading is-required">
      {{ t('shell.modal.addOrganization.field.name') }}
    </label>
    <AppInput
      id="organization-name"
      v-model="name"
      :invalid="Boolean(error)"
      :placeholder="t('shell.modal.addOrganization.field.name')"
    />
    <small v-if="error" class="mt-1 block text-danger" role="alert">{{ error }}</small>
  </AppModal>
</template>
